/**
 * Labstep
 *
 * @module grid/Index/coldefs/entityLink
 * @desc Column Definition for a link to an entity
 */

import type { ValueGetterParams } from 'ag-grid-community';
import { MetadataPreview } from 'labstep-web/components/Metadata/Preview';
import { EntityReadEntityContainer } from 'labstep-web/containers/Entity/Read/Entity';
import { DataGridPlaceholder } from 'labstep-web/core/DataGrid/Placeholder';
import { TextLink } from 'labstep-web/core/DataGrid/Text/Link';
import type { ColDef } from 'labstep-web/core/DataGrid/types';
import {
  getEditable,
  getEntityDefault,
  setValue,
} from 'labstep-web/core/DataGrid/utils';
import type { TemplatableEntity } from 'labstep-web/models';
import type { Device } from 'labstep-web/models/device.model';
import type { Entity } from 'labstep-web/models/entity.model';
import type { OrderRequest } from 'labstep-web/models/order-request.model';
import type { PurchaseOrder } from 'labstep-web/models/purchase-order.model';
import type { Resource } from 'labstep-web/models/resource.model';
import type { ResourceItem } from 'labstep-web/models/resource-item.model';
import styles from './styles.module.scss';
import type { colDefEntityLinkType } from './types';

/**
 * Column definition for entity link
 * @param getNestedEntity Custom fn to get entity as nested field
 * @returns Column definition
 */
const colDefEntityLink: colDefEntityLinkType = (options = {}) => {
  const {
    getNestedEntity,
    getEntityPreviewProps,
    placeholder,
    noLink,
    getMetadataPreviewEntity,
  } = options;
  const getEntity = getNestedEntity || getEntityDefault;
  const cellRenderer: ColDef<Entity>['cellRenderer'] = (params) => {
    const entity = getEntity(params);
    if (!entity) {
      return placeholder ? (
        <DataGridPlaceholder
          params={params}
          editable={getEditable(getNestedEntity)}
          children={placeholder}
        />
      ) : null;
    }
    const { entityName, idAttr } = entity;
    const children = (showMetadataPreview: boolean) => (
      <EntityReadEntityContainer entityName={entityName} id={idAttr}>
        {({ entity: entityRead }): JSX.Element => (
          <div className={styles.cellRenderer}>
            <TextLink
              entity={entityRead as TemplatableEntity}
              disabled={noLink}
              entityPreviewProps={getEntityPreviewProps?.(
                entityRead as TemplatableEntity,
              )}
            />
            {showMetadataPreview && (
              <small>
                <MetadataPreview
                  entity={
                    (getMetadataPreviewEntity?.(
                      entityRead as any,
                    ) as any) || (entityRead as TemplatableEntity)
                  }
                />
              </small>
            )}
          </div>
        )}
      </EntityReadEntityContainer>
    );
    return children(false);
  };

  return {
    colId: 'entityLink',
    cellRenderer,
    editable: getEditable(getNestedEntity),
    valueGetter: (params: ValueGetterParams): string | null =>
      (
        getEntity(params) as
          | Device
          | OrderRequest
          | Resource
          | ResourceItem
          | PurchaseOrder
      )?.name || null,
    valueSetter: (params: any): boolean => {
      const entity = getEntity(params);
      if (!entity) {
        return false;
      }
      return setValue('name', params.newValue, {
        entityName: entity.entityName,
        id: entity.id,
      });
    },
    valueClearer: () => undefined,
    autoHeight: true,
  };
};

export default colDefEntityLink;
